/**
 * @file bookingStepsSection.component.tsx
 * @path apps/website/src/components/sections/bookingStepsSection.component.tsx
 */

import { sectionSpacing } from "@/config";
import { Button } from "@sirbastians/ui";
import { cn } from "@sirbastians/utils";
import { ArrowUpRight, Sparkle } from "lucide-react";
import { Link } from "react-router-dom";

const tickerItems = [
  "On-Time Airport Transfers",
  "Safe Student Transport",
  "Custom Private Tours",
  "Professional Drivers",
  "Easy Online Booking",
  "Safe & Comfortable",
];

const bookingSteps = [
  {
    step: "01.",
    title: "Choose Your Service",
    description:
      "Pick from airport transfers, tours, or private hire. Add extras like activities or additional stops to make it yours.",
  },
  {
    step: "02.",
    title: "See Your Price",
    description:
      "Enter your trip details and get your exact price instantly. No surprises, no hidden costs.",
  },
  {
    step: "03.",
    title: "Pay Online",
    description:
      "Review everything, then pay securely. Takes less than 2 minutes to complete.",
  },
  {
    step: "04.",
    title: "Relax & Enjoy",
    description:
      "Get instant confirmation. Your driver will arrive on time, ready to go.",
  },
];

export const BookingStepsSection = () => {
  return (
    <section
      data-section="Booking Steps"
      data-nav-label="How It Works"
      className={cn(
        sectionSpacing,
        "py-24 bg-background text-foreground",
      )}
    >
      {/* --- 1. Scrolling Ticker --- */}
      <div className="relative w-full overflow-hidden border-y border-border py-6">
        {/* Gradients using theme background variable */}
        <div className="pointer-events-none absolute left-0 top-0 h-full w-24 bg-gradient-to-r from-background to-transparent z-10" />
        <div className="pointer-events-none absolute right-0 top-0 h-full w-24 bg-gradient-to-l from-background to-transparent z-10" />

        <h2 className="text-3xl font-bold font-montserrat sm:text-4xl text-center mb-10 px-4">
          Quick & Easy Booking
        </h2>

        <div className="flex animate-marquee-slow">
          {[...tickerItems, ...tickerItems].map((item, index) => (
            <div
              key={index}
              className="flex items-center shrink-0 ml-4 sm:ml-6"
            >
              <Sparkle className="h-4 w-4 text-accent fill-accent mr-4 sm:mr-6" />
              <span className="text-lg sm:text-xl font-semibold font-montserrat text-foreground whitespace-nowrap">
                {item}
              </span>
            </div>
          ))}
        </div>
      </div>

      <div className="mx-auto mt-16 lg:mt-20">
        <div className="flex flex-col gap-0 lg:flex-row lg:gap-12">
          {/* Left Column: Intro */}
          <div className="lg:w-1/3 shrink-0 p-2 lg:p-4 text-center lg:text-left">
            <h3 className="text-3xl font-bold font-montserrat text-foreground mt-8 sm:my-8">
              4 Easy Steps
            </h3>
            <p className="text-muted-foreground mb-8 max-w-sm mx-auto lg:mx-0 text-base md:text-lg">
              Book your ride in minutes. Our simple process makes it easy to get
              from A to B in comfort and style.
            </p>
            <Link to="/experiences">
              <Button
                variant="outline"
                size="lg"
                className="rounded-full px-8 group border-primary text-primary hover:bg-primary hover:text-primary-foreground transition-all duration-300"
              >
                Start Booking
                <ArrowUpRight className="h-5 w-5 ml-2 transition-transform duration-300 group-hover:-translate-y-1 group-hover:translate-x-1" />
              </Button>
            </Link>
          </div>

          {/* Right Column: Steps Grid 
              Using bg-border and gap-px to create consistent thin borders between cards
          */}
          <div className="w-full grid grid-cols-1 sm:grid-cols-2 gap-px bg-border border border-border mt-12 lg:mt-0 lg:border-t-0">
            {bookingSteps.map((step) => (
              <div
                key={step.step}
                className="bg-card p-8 md:p-10 transition-colors duration-300 hover:bg-secondary/30"
              >
                <p className="text-5xl md:text-6xl font-bold font-montserrat text-accent mb-6">
                  {step.step}
                </p>
                <h4 className="text-2xl font-bold font-montserrat text-card-foreground mb-4">
                  {step.title}
                </h4>
                <p className="text-muted-foreground text-sm sm:text-base leading-relaxed">
                  {step.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};
